import React, { useEffect, useRef, useState } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, FolderKanban, Inbox, Euro, Receipt, LifeBuoy, FileText, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const CHART_H = 260;

function StatSkeleton() {
  return (
    <div className="surface p-5" aria-hidden="true">
      <div className="flex items-center justify-between">
        <div className="h-4 w-20 rounded bg-secondary animate-pulse" />
        <div className="h-4 w-4 rounded bg-secondary animate-pulse" />
      </div>
      <div className="mt-3 h-8 w-24 rounded bg-secondary animate-pulse" />
    </div>
  );
}

function PanelSkeleton({ title }) {
  return (
    <div className="surface p-6" aria-hidden="true">
      <h3 className="font-display font-bold mb-4">{title}</h3>
      <div className="rounded-lg bg-secondary animate-pulse" style={{ height: CHART_H }} />
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useI18n();
  const de = lang === "de";
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return; // fetch once; also prevents StrictMode double-fetch
    fetchedRef.current = true;
    api.get("/dashboard/admin")
      .then((r) => setD(r.data))
      .catch(() => setError(true));
  }, []);

  const loading = d === null && !error;

  const cards = d ? [
    [t("admin.revenue"), `${(d.revenue || 0).toLocaleString()} €`, Euro],
    [t("admin.customers"), d.customers ?? 0, Users],
    [t("admin.newQuotes"), d.new_quotes ?? 0, Inbox],
    [t("admin.projects"), d.active_projects ?? 0, FolderKanban],
    [t("admin.offers"), d.pending_offers ?? 0, FileText],
    [t("admin.orders"), d.orders ?? 0, ShoppingBag],
    [de ? "Offene Rechnungen" : "Unpaid invoices", d.unpaid_invoices ?? 0, Receipt],
    [de ? "Offene Tickets" : "Open tickets", d.open_tickets ?? 0, LifeBuoy],
  ] : [];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-black tracking-tight">{t("admin.dashboard")}</h1>

      {/* Stat cards — skeleton keeps identical dimensions while loading */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loading
          ? Array.from({ length: 8 }).map((_, i) => <StatSkeleton key={i} />)
          : cards.map(([label, val, Icon]) => (
              <div key={label} className="surface p-5" data-testid={`admin-stat-${label}`}>
                <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{label}</span><Icon size={18} className="text-primary" /></div>
                <div className="font-display text-2xl md:text-3xl font-black mt-3">{val}</div>
              </div>
            ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {loading ? (
          <>
            <PanelSkeleton title={de ? "Umsatz (letzte Monate)" : "Revenue (recent months)"} />
            <PanelSkeleton title={de ? "Letzte Zahlungen" : "Recent payments"} />
          </>
        ) : (
          <>
            <div className="surface p-6">
              <h3 className="font-display font-bold mb-4">{de ? "Umsatz (letzte Monate)" : "Revenue (recent months)"}</h3>
              <ResponsiveContainer width="100%" height={CHART_H}>
                <LineChart data={d.revenue_by_month || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Line type="monotone" dataKey="revenue" stroke="hsl(21 100% 50%)" strokeWidth={2.5} dot={{ fill: "hsl(21 100% 50%)" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="surface p-6">
              <h3 className="font-display font-bold mb-4">{de ? "Letzte Zahlungen" : "Recent payments"}</h3>
              <div className="space-y-2" style={{ minHeight: CHART_H }}>
                {(d.recent_payments || []).map((p) => (
                  <div key={p.id} className="flex justify-between items-center text-sm border-b border-border pb-2">
                    <span className="text-muted-foreground">{p.user_email}</span>
                    <span className="font-semibold">{(p.amount || 0).toFixed(2)} {(p.currency || "").toUpperCase()}</span>
                  </div>
                ))}
                {(!d.recent_payments || d.recent_payments.length === 0) && <p className="text-sm text-muted-foreground">{t("common.none")}</p>}
              </div>
            </div>
          </>
        )}
      </div>

      {error && <p className="text-sm text-destructive" data-testid="admin-dashboard-error">{de ? "Dashboard-Daten konnten nicht geladen werden." : "Failed to load dashboard data."}</p>}
    </div>
  );
}
